import mapStyle from "@assets/mapStyle.json";
import { t } from "@lingui/macro";

import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { createRoot } from "react-dom/client";
import { useSearchParams } from "react-router-dom";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { I18nProvider, useLingui } from "@lingui/react";
import { IconButton, Inset, Tooltip, Text, Flex } from "@radix-ui/themes";
import Comment from "@components/questions/Comment";
import { useSurveyStore } from "@store/survey";
import MapPopover from "@components/MapPopover";
import SchoolSummary from "@components/SchoolSummary";
import Legend from "@components/Legend";
import BottomLeftMapDiv from "@components/BottomLeftMapDiv";
import BottomCenterMapDiv from "@components/BottomCenterMapDiv";
import TopLeftMapDiv from "@components/TopLeftMapDiv";
import SchoolSearch from "@components/SchoolSearch";
import ClusterSummary from "@components/ClusterSummary";
import BottomRightMapDiv from "@components/BottomRightMapDiv";
import { recordEvent } from "@api/survey";
import { Trans } from "@lingui/react";
import {
  GlobeIcon,
  SewingPinFilledIcon,
  DrawingPinIcon,
} from "@radix-ui/react-icons";

export default function Map() {
  const queryClient = useQueryClient();
  const mapContainer = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const map = useRef(null);
  const setMapRef = useSurveyStore((state) => state.setMapRef);
  const setMapIsLoaded = useSurveyStore((state) => state.setMapIsLoaded);
  const sessionUUID = useSurveyStore((state) => state.sessionUUID);
  const currentSurveySection = useSurveyStore().currentSurveySection;
  const surveySectionLayers = useSurveyStore(
    (state) => state.surveySectionLayers,
  );
  const appendMapMarker = useSurveyStore((state) => state.appendMapMarker);
  const appendMapComponentRef = useSurveyStore(
    (state) => state.appendMapComponentRef,
  );
  const satellite = useSurveyStore((state) => state.satellite);
  const setSatellite = useSurveyStore((state) => state.setSatellite);
  const commentQuestion = useSurveyStore((state) => state.commentQuestion);
  const { i18n } = useLingui();

  useEffect(() => {
    let protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);
    if (map.current) setMapRef(map);
    return () => {
      maplibregl.removeProtocol("pmtiles");
    };
  }, [setMapRef]);

  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    let initial_longitude = -87.967368;
    let initial_latitude = 43.05772;
    let initial_zoom = 11.2;

    if (searchParams.has("latitude") && searchParams.has("longitude")) {
      initial_longitude = parseFloat(searchParams.get("longitude"));
      initial_latitude = parseFloat(searchParams.get("latitude"));
    }

    if (searchParams.has("zoom")) {
      initial_zoom = parseFloat(searchParams.get("zoom"));
    }

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [initial_longitude, initial_latitude],
      zoom: initial_zoom,
      maxZoom: 20.9,
      minZoom: 8.5,
      pitchWithRotate: false,
      touchPitch: false,
      dragRotate: false,
    });

    map.current.on("load", () => {
      setMapRef(map);
      setMapIsLoaded(true);
    });

    map.current.scrollZoom.setWheelZoomRate(1 / 300);
    map.current.scrollZoom.setZoomRate(1 / 100);
    map.current.addControl(new maplibregl.NavigationControl());

    return () => {
      ["moveend", "click"].forEach((action) => {
        map.current?.off(action.action, (e) => {
          action.handler(e, map);
        });
      });
    };
  }, [map, searchParams, setMapRef]);

  useEffect(() => {
    const updateMapLocationSearchParams = () => {
      if (map.current) {
        const { lng, lat } = map.current.getCenter();
        const zoom = map.current.getZoom();
        const currentParams = new URLSearchParams(searchParams.toString());

        if (
          currentParams.get("latitude") !== lat.toString() ||
          currentParams.get("longitude") !== lng.toString() ||
          currentParams.get("zoom") !== zoom.toString()
        ) {
          currentParams.set("latitude", lat.toFixed(6));
          currentParams.set("longitude", lng.toFixed(6));
          currentParams.set("zoom", zoom.toFixed(1));
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}?${currentParams.toString()}`,
          );
          const bounds = map.current.getBounds();
          recordEvent({
            eventType: "map_move",
            eventData: {
              latitude: lat,
              longitude: lng,
              zoom: zoom,
              bounds: bounds,
            },
            sessionUUID: sessionUUID,
            surveySectionSlug: currentSurveySection,
          });
        }
      }
    };

    if (map.current) {
      map.current.on("moveend", updateMapLocationSearchParams);
    }

    return () => {
      if (map.current) {
        map.current.off("moveend", updateMapLocationSearchParams);
      }
    };
  });

  useEffect(() => {
    const renderMapCommentQuestion = (e) => {
      const commentQuestion = useSurveyStore.getState().commentQuestion;
      if (map.current && commentQuestion) {
        const el = document.createElement("div");
        el.id = `comment-${Math.random().toString(36).slice(2, 9)}`;
        el.addEventListener("click", (e) => e.stopPropagation(), false);

        const rootRef = createRoot(el);
        let marker = new maplibregl.Marker({ element: el });
        marker.setLngLat([e.lngLat.lng, e.lngLat.lat]).addTo(map.current);

        const hoverFeature = useSurveyStore.getState().hoverFeature;
        const sessionUUID = useSurveyStore.getState().sessionUUID;

        rootRef.render(
          <I18nProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <Comment
                marker={marker}
                rootRef={rootRef}
                question={commentQuestion}
                sessionUUID={sessionUUID}
                hoverFeature={hoverFeature}
              />
            </QueryClientProvider>
          </I18nProvider>,
        );

        appendMapMarker(marker);
        appendMapComponentRef(rootRef);
      }
    };

    if (map.current) {
      map.current.on("click", renderMapCommentQuestion);
    }

    return () => {
      if (map.current) {
        map.current.off("click", renderMapCommentQuestion);
      }
    };
  }, [map, i18n.locale, queryClient]);

  useEffect(() => {
    const renderFeaturePopover = (e) => {
      const commentQuestion = useSurveyStore.getState().commentQuestion;
      if (commentQuestion) return;

      const surveySectionLayers = useSurveyStore.getState().surveySectionLayers;
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: surveySectionLayers.map((layer) => layer.layer.id),
      });

      if (!(map.current && features.length > 0)) return;

      const setSelectedSchool = useSurveyStore.getState().setSelectedSchool;

      const el = document.createElement("div");
      el.id = `summary-${Math.random().toString(36).slice(2, 9)}`;
      el.addEventListener("click", (e) => e.stopPropagation(), false);
      const rootRef = createRoot(el);
      let marker = new maplibregl.Marker({ element: el });
      marker.setLngLat(e.lngLat).addTo(map.current);

      const feature = features[0];
      setSelectedSchool(feature);

      rootRef.render(
        <I18nProvider i18n={i18n}>
          <MapPopover marker={marker} rootRef={rootRef}>
            {feature.source === "mps_schools" ? (
              <SchoolSummary />
            ) : (
              <ClusterSummary />
            )}
          </MapPopover>
        </I18nProvider>,
      );

      appendMapMarker(marker);
      appendMapComponentRef(rootRef);
    };

    if (map.current) {
      map.current.on("click", renderFeaturePopover);
    }

    return () => {
      if (map.current) {
        map.current.off("click", renderFeaturePopover);
      }
    };
  }, [map, i18n.locale, queryClient]);

  return (
    <div ref={mapContainer}>
      <TopLeftMapDiv>
        <SchoolSearch />
      </TopLeftMapDiv>
      {surveySectionLayers?.length > 0 && (
        <BottomLeftMapDiv>
          <Legend />
        </BottomLeftMapDiv>
      )}
      <BottomRightMapDiv>
        <Inset clip="padding-box">
          <Tooltip
            content={
              satellite
                ? t({
                    id: "map.view_map",
                  })
                : t({
                    id: "map.view_satellite",
                  })
            }
          >
            <IconButton
              variant="ghost"
              size="3"
              m="3"
              onClick={() => setSatellite(!satellite)}
            >
              {satellite ? (
                <SewingPinFilledIcon width="22" height="22" />
              ) : (
                <GlobeIcon width="22" height="22" />
              )}
            </IconButton>
          </Tooltip>
        </Inset>
      </BottomRightMapDiv>
      {commentQuestion && (
        <BottomCenterMapDiv color="var(--accent-9)">
          <Flex direction="row" align="center" justify="center" p="2" gap="3">
            <DrawingPinIcon width="22px" height="22px" />
            <Text size="2">
              <Trans id="Map.clicktocomment">
                Click on the map to leave a comment
              </Trans>
            </Text>
          </Flex>
        </BottomCenterMapDiv>
      )}
    </div>
  );
}
